import {
  type BillingInterval,
  isBillingInterval,
  type SelfServePlanKey,
} from "@giftforward/entitlements";
import { useCallback, useState } from "react";

/**
 * "setup" is the stand-in shown while the organization is created, and "payment" is where a Pro or
 * Pro Plus client completes the subscription. Neither is a numbered step — the progress bar holds at the
 * plan step's number through both.
 */
export type OnboardingStep = "profile" | "plan" | "setup" | "payment" | "team";

interface OnboardingFormData {
  firstName: string;
  lastName: string;
  orgName: string;
  avatarFile: File | null;
  avatarPreviewUrl: string | null;
  selectedPlan: "free" | SelfServePlanKey;
  /**
   * Which of the paid plan's offers the payment step charges: Monthly or Annual for Pro, Annual
   * by default. A plan sold one way (Pro Plus) ignores it — checkout takes that plan's only offer.
   */
  selectedInterval: BillingInterval;
  invitedEmails: string[];
  /**
   * The Platform Terms version shown on the profile step, and whether it was accepted.
   *
   * The VERSION is held rather than re-read at submit time: the client agreed to the text they were
   * shown, so that is what gets sent. If a new version is published mid-signup the server refuses it
   * and the screen re-asks — which is the correct outcome, and not one the client can be walked
   * into by a background refetch swapping the number under them.
   */
  termsVersion: string | null;
  termsAccepted: boolean;
}

interface OnboardingState {
  step: OnboardingStep;
  formData: OnboardingFormData;
  isSubmitting: boolean;
  error: string | null;
  /**
   * The setup step has created this client's organization.
   *
   * Persisted, because the two things that read it — the wizard's invited-user redirect and
   * `useOnboardingSubmit`'s decision to keep going after `alreadyOnboarded` — were both asking a
   * per-mount `useRef`, and a ref is empty again after a remount. From that moment the wizard
   * cannot tell a client it onboarded itself from an invited user who wandered in, and treats the
   * former as the latter: redirect to the dashboard, mid-flow and unpaid.
   */
  hasOrganization: boolean;
}

const INITIAL_FORM_DATA: OnboardingFormData = {
  firstName: "",
  lastName: "",
  orgName: "",
  avatarFile: null,
  avatarPreviewUrl: null,
  selectedPlan: "free",
  selectedInterval: "year",
  invitedEmails: ["", ""],
  termsVersion: null,
  termsAccepted: false,
};

const STORAGE_KEY = "gfg_onboarding_state";

function loadPersistedState(): Partial<OnboardingState> | null {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    // Can't persist File objects, so avatarFile is always null on reload.
    return {
      // "setup" is driven by a request in flight, and a reload has no request to resume. Resuming
      // there would strand the client on a spinner that nothing will ever resolve, so send them
      // back to the choice that starts it — picking a plan again is idempotent.
      step: parsed.step === "setup" ? "plan" : parsed.step,
      hasOrganization: parsed.hasOrganization ?? false,
      formData: {
        ...parsed.formData,
        avatarFile: null,
        avatarPreviewUrl: null,
        selectedPlan: parsed.formData.selectedPlan ?? "free",
        selectedInterval: isBillingInterval(parsed.formData.selectedInterval)
          ? parsed.formData.selectedInterval
          : "year",
        invitedEmails: parsed.formData.invitedEmails ?? ["", ""],
      },
    };
  } catch {
    return null;
  }
}

function persistState(state: OnboardingState) {
  try {
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        step: state.step,
        hasOrganization: state.hasOrganization,
        formData: {
          firstName: state.formData.firstName,
          lastName: state.formData.lastName,
          orgName: state.formData.orgName,
          selectedPlan: state.formData.selectedPlan,
          selectedInterval: state.formData.selectedInterval,
          invitedEmails: state.formData.invitedEmails,
        },
      }),
    );
  } catch {
    // sessionStorage not available — silently fail.
  }
}

/**
 * useOnboardingState — manages the onboarding wizard's step state and form data.
 *
 * Persists the step, the form data and whether the organization exists to sessionStorage, so a
 * reload or a remount mid-wizard resumes where the client was rather than starting over.
 */
export function useOnboardingState() {
  const persisted = loadPersistedState();

  const [state, setState] = useState<OnboardingState>({
    step: persisted?.step ?? "profile",
    formData: {
      ...INITIAL_FORM_DATA,
      ...persisted?.formData,
    },
    isSubmitting: false,
    error: null,
    hasOrganization: persisted?.hasOrganization ?? false,
  });

  const updateFormData = useCallback((updates: Partial<OnboardingFormData>) => {
    setState((prev) => {
      const next = {
        ...prev,
        formData: { ...prev.formData, ...updates },
        error: null,
      };
      persistState(next);
      return next;
    });
  }, []);

  const setStep = useCallback((step: OnboardingStep) => {
    setState((prev) => {
      const next = { ...prev, step, error: null };
      persistState(next);
      return next;
    });
  }, []);

  /** Records that setup created the organization. Persisted; see OnboardingState. */
  const markOrganizationCreated = useCallback(() => {
    setState((prev) => {
      const next = { ...prev, hasOrganization: true };
      persistState(next);
      return next;
    });
  }, []);

  const setSubmitting = useCallback((isSubmitting: boolean) => {
    setState((prev) => ({ ...prev, isSubmitting }));
  }, []);

  const setError = useCallback((error: string | null) => {
    setState((prev) => ({ ...prev, error }));
  }, []);

  /** Clear persisted state (call on wizard completion). */
  const clearPersistedState = useCallback(() => {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      // silent
    }
  }, []);

  /** Validate the profile step. Returns an error message or null if valid. */
  const validateProfile = useCallback((): string | null => {
    const { firstName, lastName, orgName, termsAccepted, termsVersion } = state.formData;
    if (!firstName.trim()) return "First name is required.";
    if (!lastName.trim()) return "Last name is required.";
    if (!orgName.trim()) return "Organization name is required.";
    // Checked here as well as by the disabled button: the button is a convenience, this is the gate.
    if (!termsAccepted || !termsVersion) {
      return "Please accept the Platform Terms & Conditions to continue.";
    }
    return null;
  }, [state.formData]);

  return {
    step: state.step,
    formData: state.formData,
    isSubmitting: state.isSubmitting,
    error: state.error,
    hasOrganization: state.hasOrganization,
    markOrganizationCreated,
    updateFormData,
    setStep,
    setSubmitting,
    setError,
    clearPersistedState,
    validateProfile,
  };
}
